// OpsAlo docs in Documents: list (only ones you can see), create, move, delete, and sharing.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';

export interface VenueDoc {
  id: string;
  title: string;
  folder_id: string | null;
  general_access: 'restricted' | 'view' | 'edit';
  ai_created: boolean;
  created_by: string | null;
  updated_at: string;
}
export interface DocShare { doc_id: string; role: string | null; user_id: string | null; can_edit: boolean }

const MIGRATION = 'Docs need the latest database update (20261028050000_venue_docs.sql).';
// Only a missing table/column means the database update hasn't been run; anything else is shown as it is.
export const docError = (m: string) => (/(relation|column) "?[\w.]*venue_doc[\w.]*"? does not exist|schema cache/i.test(m) ? MIGRATION : m);

export function useVenueDocs(venueId: string) {
  const [docs, setDocs] = useState<VenueDoc[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error: e } = await supabase.from('venue_docs').select('id, title, folder_id, general_access, ai_created, created_by, updated_at')
      .eq('venue_id', venueId).order('updated_at', { ascending: false });
    if (e) { setError(docError(e.message) === MIGRATION ? null : e.message); setDocs([]); return; } // not set up yet: just no docs
    setError(null);
    setDocs((data ?? []) as VenueDoc[]);
  }, [venueId]);
  useEffect(() => { void load(); }, [load]);

  const create = useCallback(async (title: string, folderId: string | null, userId: string) => {
    const { data, error: e } = await supabase.from('venue_docs').insert({ venue_id: venueId, title: title.trim() || 'Untitled', folder_id: folderId, created_by: userId, seeded: true })
      .select('id').single();
    if (e) return { error: docError(e.message), id: null };
    await load();
    return { error: null, id: (data as { id: string }).id };
  }, [venueId, load]);

  const move = useCallback(async (id: string, folderId: string | null) => {
    const { error: e } = await supabase.from('venue_docs').update({ folder_id: folderId }).eq('id', id);
    if (!e) await load();
    return { error: e ? docError(e.message) : null };
  }, [load]);

  const remove = useCallback(async (id: string) => {
    const { error: e } = await supabase.from('venue_docs').delete().eq('id', id);
    if (!e) await load();
    return { error: e ? docError(e.message) : null };
  }, [load]);

  return { docs, error, reload: load, create, move, remove };
}
